import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { WorkableJobData } from './lib/types.js';
import { WorkableController } from './lib/workable-controller.js';
import { styles } from './lib/styles.css.js';

@customElement('workable-widget')
export class WorkableJobsWidget extends LitElement {
  static styles = [styles];

  @property({ type: String })
  account?: string;

  @state()
  private locationFilter?: string;

  private jobs?: WorkableController;

  connectedCallback(){
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    if (this.account === undefined) {
      throw new Error("account-id attribute must be set");
    }
    this.jobs = new WorkableController(this, this.account)
  }

  // eslint-disable-next-line class-methods-use-this
  private _renderSingleJob(job: WorkableJobData){
    return html`
      <div class="job">
        <a href="${job.url}" 
          target="_blank" 
          class="job-title" 
          data-job-country="${job.country}" 
          data-job-title="${job.title}"
          @click=${this._handleJobClick}>
          ${job.title}
        </a>
        <span class="job-location">${job.city}, ${job.country}</span>
      </div>
    `;
  }

  private _updateLocationFilter(e: Event) {
    // @ts-ignore
    const selectedLocation = e.target.value;
    this.locationFilter = selectedLocation;
    this.requestUpdate();
  }

  private _renderJobs(jobs: Array<WorkableJobData>) {
    const groupedJobs = WorkableJobsWidget._groupJobsByCountry(jobs);
    const locations = Array.from(groupedJobs.keys());

    if (this.locationFilter !== '' && this.locationFilter !== undefined && this.locationFilter !== null) {
      jobs = jobs.filter((job) => job.country  === this.locationFilter);
    }

    const sectionCode: Array<TemplateResult<1>> = [];

    sectionCode.push(html`
      <header>
          <h3 class="widget-heading">Current Openings</h3>

          <div>
            <label for="country-select">Filter by country</label>

            <select name="countries" id="country-select"  @change=${this._updateLocationFilter}> 
              <option value="">All Locations</option>
              ${locations.map((job) => html`<option value="${job}">${job}</option>`)}
            </select>
          </div>
        </header>
    `);

    sectionCode.push(html`
      <ul class="jobs-list">
        ${jobs.map((job) => html`<li>${this._renderSingleJob(job)}</li>`)}
      </ul>`);

      return sectionCode;
  
  }

  // eslint-disable-next-line class-methods-use-this
  private _renderLoadingState(){
    return html`
      <p>Loading...</p>
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  private _renderErrorState(error: any){
    // eslint-disable-next-line no-console
    console.error(error);
    return html`
      <p>${error}</p>
    `;
  }

  render() {
    if (this.jobs !== undefined) {
      return html`
        ${this.jobs.render({
          complete: (jobsData: Array<WorkableJobData>) => this._renderJobs(jobsData),
          pending: () => this._renderLoadingState(),
          error: (e: any) => this._renderErrorState(e)
        })}
      `;
    }
    
    return html``;
  }

  // Takes an Array<WorkableJobData> and returns a Map<string, Array<WorkableJobData>> which groups
  // the various job listings by their country location which is the key
  private static _groupJobsByCountry(jobs: Array<WorkableJobData>){
    const groupedMap = jobs.reduce(
      (entryMap, e) => entryMap.set(e.country, [...entryMap.get(e.country)||[], e]),
      new Map<string, Array<WorkableJobData>>()
    );

    return groupedMap;
  }

  private _handleJobClick(clickEvent: MouseEvent){
    if (clickEvent.target instanceof HTMLElement) { 
      const detail = { jobTitle: clickEvent.target.dataset.jobTitle, jobCountry: clickEvent.target.dataset.jobCountry };
      const customEvent = new CustomEvent('viewjoblisting', {detail, bubbles: true, composed: true, cancelable: false});
      this.dispatchEvent(customEvent);

      if('dataLayer' in window && typeof(window['dataLayer']) === 'object'){
        window.dataLayer.push({
          'event': 'view_job_listing',
          'jobTitle': clickEvent.target.dataset.jobTitle,
          'jobCountry': clickEvent.target.dataset.jobCountry
        });
      }
    }
  }
}